import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, MapPin } from 'lucide-react';
import { SEO, localBusinessSchema, organizationSchema, websiteSchema } from '../components/SEO';
import { STATIC_SEO } from '../data/seoConfig';
import { Reveal, Stagger, StaggerItem } from '../components/Reveal';
import EditorialMarquee from '../components/EditorialMarquee';
import CTASection from '../components/CTASection';
import FAQSection from '../components/FAQSection';
import { ServiceCard, ProjectCard, LocationCard } from '../components/cards';
import { SITE, IMAGES, PROCESS_STEPS } from '../data/site';
import { SERVICES } from '../data/services';
import { PROJECTS } from '../data/projects';
import { LOCATIONS } from '../data/locations';

const HERO_LINES = ['Create an Outdoor Space', "You'll Love", 'Coming Home To.'];

const KineticHeadline = () => {
  const reduce = useReducedMotion();
  return (
    <h1 className="font-serif text-[11.5vw] font-medium leading-[1.0] tracking-tight text-paper sm:text-6xl lg:text-6xl xl:text-[4.4rem]">
      {HERO_LINES.map((line, i) => (
        <span key={line} className="mask-line pb-1">
          <motion.span
            className="block"
            initial={reduce ? false : { y: '110%' }}
            animate={reduce ? {} : { y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
          >
            {i === 1 ? <em className="text-bronzelight">{line}</em> : line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  return (
    <section ref={ref} data-testid="hero-section" className="noise-dark relative overflow-hidden bg-graphite">
      <div className="container-site relative z-10 pb-16 pt-32 md:pt-40 lg:pb-20">
        <div className="grid items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-bronzelight"
            >
              <MapPin className="h-3.5 w-3.5" /> Serving Omaha &amp; Surrounding Nebraska Communities
            </motion.p>
            <KineticHeadline />
            <motion.p
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-8 max-w-xl text-lg leading-relaxed text-paper/70"
            >
              Nebraska Backyard Living designs and builds exceptional outdoor spaces — custom kitchens, sunrooms, patios, decks, and fire features — for homeowners across the Omaha metro and surrounding communities. Locally owned. 22+ years. One accountable team from first sketch to final walkthrough.
            </motion.p>
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to="/get-a-quote/" data-testid="hero-quote-cta" className="btn-primary">Get a Free Quote</Link>
              <Link to="/projects/" data-testid="hero-work-cta" className="btn-outline-light">Explore Our Work</Link>
            </motion.div>
          </div>
          <div className="hidden lg:col-span-5 lg:block">
            <motion.div
              initial={reduce ? false : { opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="img-frame aspect-[4/5] border border-paper/10 p-3">
                <motion.div style={reduce ? undefined : { y: imgY }} className="h-[112%] w-full">
                  <img src={IMAGES.hero.src} alt={IMAGES.hero.alt} className="h-full w-full object-cover" fetchpriority="high" />
                </motion.div>
              </div>
              <div className="absolute -bottom-5 -left-5 border border-line bg-paper px-6 py-4" data-testid="hero-trust-badge">
                <p className="font-serif text-3xl font-semibold text-ink">{SITE.years}+</p>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted2">Years of Craftsmanship</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustStrip = () => (
  <section data-testid="trust-strip" className="border-b border-line bg-paper">
    <div className="container-site grid grid-cols-2 gap-px bg-line lg:grid-cols-4">
      {[
        ['Local Nebraska Expertise', 'Deep knowledge of our climate, codes, and communities'],
        ['Thoughtful Design', 'Custom plans shaped around how you live outdoors'],
        ['Quality Craftsmanship', 'Premium materials, meticulous detail, proven methods'],
        ['Complete Outdoor Solutions', 'One accountable team, design through build'],
      ].map(([title, sub], i) => (
        <div key={title} className="bg-paper px-6 py-8 lg:py-10">
          <Reveal delay={i * 0.08}>
            <p className="font-serif text-xl font-medium text-ink">{title}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-muted2">{sub}</p>
          </Reveal>
        </div>
      ))}
    </div>
  </section>
);

const ServicesSection = () => (
  <section data-testid="home-services" className="py-24 md:py-32">
    <div className="container-site">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="overline-label">What We Do</p>
            <h2 className="mt-3 max-w-2xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl">
              Everything your backyard needs, designed as one space
            </h2>
          </div>
          <Link to="/services/" data-testid="services-view-all" className="link-underline text-sm uppercase tracking-[0.2em]">
            All Services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
      <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.slice(0, 6).map((s) => (
          <StaggerItem key={s.slug}><ServiceCard service={s} /></StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const ProjectsSection = () => (
  <section data-testid="home-projects" className="border-y border-line bg-paper2/50 py-24 md:py-32">
    <div className="container-site">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="overline-label">Our Work</p>
            <h2 className="mt-3 max-w-2xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl">
              Outdoor spaces built for real Nebraska life
            </h2>
          </div>
          <Link to="/projects/" data-testid="projects-view-all" className="link-underline text-sm uppercase tracking-[0.2em]">
            View All Projects <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
      <Stagger className="mt-14 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {PROJECTS.map((p) => (
          <StaggerItem key={p.slug}><ProjectCard project={p} /></StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const CHAPTERS = [
  { title: 'Local Understanding', text: 'Two decades of building in Nebraska means we design for freeze-thaw cycles, July heat, sudden storms, and the way families here actually live outside. Climate is not an afterthought — it is the brief.' },
  { title: 'Design-First Thinking', text: 'We do not sell features; we compose spaces. Every project starts with listening, then a custom plan where kitchen, patio, cover, and fire work together as one environment.' },
  { title: 'Craftsmanship That Lasts', text: 'Premium materials, proper base work, meticulous detail. We build outdoor spaces the way we have built our reputation over 22 years — to stand the test of time.' },
  { title: 'One Accountable Team', text: 'From first sketch to final walkthrough, you work with the same local team. Transparent communication, honest pricing, and a phone number that gets answered.' },
];

const Manifesto = () => (
  <section data-testid="manifesto-section" className="py-24 md:py-32">
    <div className="container-site">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <p className="overline-label">Why Nebraska Backyard Living</p>
              <h2 className="mt-3 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl">
                Built on 22 years of getting it right
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-muted2">
                {SITE.name} ({SITE.legalName}) is locally owned and operated, with an {SITE.rating}. We have earned our reputation the slow way — project by project, neighbor by neighbor, across Omaha and Lincoln.
              </p>
              <Link to="/about/" data-testid="manifesto-about-link" className="btn-outline mt-8">Our Story</Link>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05} className={`border-t border-line py-10 ${i === CHAPTERS.length - 1 ? 'border-b' : ''}`}>
              <div className="flex gap-8 md:gap-12">
                <span className="font-serif text-6xl font-light leading-none text-bronzelight md:text-7xl" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h3 className="font-serif text-2xl font-medium tracking-tight text-ink md:text-3xl">{c.title}</h3>
                  <p className="mt-3 max-w-lg text-base leading-relaxed text-muted2">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section data-testid="process-section" className="noise-dark bg-graphite py-24 text-paper md:py-32">
    <div className="container-site relative z-10">
      <Reveal>
        <p className="overline-label">How It Works</p>
        <h2 className="mt-3 max-w-2xl font-serif text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
          Four steps. Zero guesswork.
        </h2>
      </Reveal>
      <Stagger className="mt-16 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        {PROCESS_STEPS.map((s) => (
          <StaggerItem key={s.num}>
            <div className="border-t border-paper/20 pt-6" data-testid={`process-step-${s.num}`}>
              <span className="font-serif text-5xl font-light italic text-bronze">{s.num}</span>
              <h3 className="mt-4 font-serif text-2xl font-medium">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-paper/60">{s.text}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const ServiceAreas = () => (
  <section data-testid="service-areas-section" className="py-24 md:py-32">
    <div className="container-site">
      <Reveal>
        <p className="overline-label">Where We Work</p>
        <h2 className="mt-3 max-w-3xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl">
          Serving homeowners across the Omaha metro &amp; surrounding Nebraska communities
        </h2>
      </Reveal>
      <Stagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {LOCATIONS.map((l) => (
          <StaggerItem key={l.slug}>
            <Link
              to={`/service-areas/${l.slug}/`}
              data-testid={`area-tile-${l.slug}`}
              className="group flex items-center justify-between border border-line bg-paper px-5 py-5 transition-colors hover:border-bronze hover:bg-paper2/60"
            >
              <div>
                <span className="font-serif text-xl font-medium text-ink transition-colors group-hover:text-bronze">{l.city}</span>
                <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-muted2">{l.region}</span>
              </div>
              <ArrowRight className="h-4 w-4 text-bronze opacity-0 transition-opacity group-hover:opacity-100" />
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const Testimonials = () => (
  <section data-testid="testimonials-section" className="border-y border-line bg-paper2/50 py-24 md:py-28">
    <div className="container-site">
      <Reveal className="mx-auto max-w-2xl text-center">
        <p className="overline-label">Client Stories</p>
        <h2 className="mt-3 font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">What our clients say</h2>
        <div className="mt-10 border border-dashed border-bronze/50 bg-paper px-8 py-12" data-testid="testimonials-placeholder">
          <p className="font-serif text-2xl italic text-ink/70">
            "Real reviews from Omaha &amp; Lincoln homeowners will live here."
          </p>
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-muted2">
            Content placeholder — client testimonials to be added
          </p>
        </div>
        <p className="mt-8 text-sm text-muted2">
          In the meantime: {SITE.years}+ years in business, locally owned, {SITE.rating}, licensed &amp; insured — and happy to connect you with past clients.
        </p>
      </Reveal>
    </div>
  </section>
);

const HOME_FAQS = [
  { q: 'What areas do you serve?', a: 'We serve the greater Omaha and Lincoln metro areas, including Millard, Papillion, La Vista, Gretna, Elkhorn, Bennington, Wahoo, and Ashland. Every project starts with a free on-site consultation at your home.' },
  { q: 'How does the free consultation work?', a: 'We visit your property, listen to how you want to use your outdoor space, assess sun, slope, and access, and discuss honest budget guidance. There is no pressure and no obligation.' },
  { q: 'How long does a typical project take?', a: 'It depends on scope — a patio may take days, while a complete outdoor living environment can take several weeks. You receive a realistic timeline before work begins and honest updates throughout.' },
  { q: 'Do you handle design, or do I need plans first?', a: 'We handle everything. Our custom design-build process takes you from first ideas through design, permitting, and construction with one accountable team.' },
  { q: 'Can my project be built in phases?', a: 'Absolutely. We can master-plan your complete backyard vision and build it in stages, so each phase connects seamlessly without rework.' },
];

const HomePage = () => (
  <>
    <SEO
      title={STATIC_SEO.home.title}
      description={STATIC_SEO.home.description}
      path="/"
      jsonLd={[localBusinessSchema, organizationSchema, websiteSchema]}
      image={IMAGES.hero.src}
    />
    <Hero />
    <EditorialMarquee dark />
    <TrustStrip />
    <ServicesSection />
    <ProjectsSection />
    <Manifesto />
    <Process />
    <ServiceAreas />
    <Testimonials />
    <section className="py-24 md:py-28">
      <div className="container-site max-w-4xl">
        <Reveal>
          <FAQSection faqs={HOME_FAQS} heading="Questions homeowners ask us" />
        </Reveal>
      </div>
    </section>
    <CTASection />
  </>
);

export default HomePage;
